/* Mitchell Pacific product template. All merchandise comes from Liquid product pickers. */
(() => {
  'use strict';
  const normalize = value => String(value ?? '').toLowerCase().trim().replace(/[“”″]/g, '"').replace(/½/g, '.5').replace(/\s+/g, '').replace(/[^a-z0-9.]/g, '');
  const buildMatches = (actual, expected) => {
    const a = normalize(actual), b = normalize(expected);
    return a === b || (['original', 'deluxe'].includes(b) && a.startsWith(b));
  };
  function optionIndex(product, explicit, hints, predicate) {
    if (!product) return -1;
    if (String(explicit || '').trim()) {
      const i = product.options.findIndex(o => normalize(o.name) === normalize(explicit));
      return i < 0 ? -2 : i;
    }
    let i = product.options.findIndex(o => hints.test(o.name));
    if (i < 0 && predicate) i = product.options.findIndex(predicate);
    return i;
  }
  function findMap(maps, state) {
    const candidates = maps.filter(m => m.capacity === state.capacity && m.security === state.security);
    return candidates.find(m => m.build === state.build) || candidates.find(m => m.build === 'Any') || null;
  }
  function carrierDimensions(map, state) {
    if (!map?.product) return {valid: false, buildIndex: -1, colorIndex: -1};
    const p = map.product;
    const colorIndex = optionIndex(p, map.colorOption, /colou?r/i);
    const buildIndex = state.capacity === 'Single' && map.build === 'Any'
      ? optionIndex(p, map.buildOption, /build|version|tube|style/i, o => o.values.some(v => buildMatches(v, map.originalValue)) && o.values.some(v => buildMatches(v, map.deluxeValue))) : -1;
    const buildValue = state.build === 'Original' ? map.originalValue : map.deluxeValue;
    const valid = colorIndex !== -2 && buildIndex !== -2 && !(state.capacity === 'Single' && map.build === 'Any' && buildIndex === -1);
    return {valid, buildIndex, colorIndex, buildValue};
  }
  function carrierVariants(map, state, selections = {}) {
    const d = carrierDimensions(map, state);
    if (!d.valid) return [];
    return map.product.variants.filter(v => v.options.every((value, i) => {
      if (i === d.buildIndex) return buildMatches(value, d.buildValue);
      return selections[i] == null || normalize(value) === normalize(selections[i]);
    }));
  }
  function kitDimensions(product, settings, state) {
    if (!product) return {valid: false, widthIndex: -1, fitIndex: -1};
    const widthIndex = optionIndex(product, settings.width_option, /width|rack.?bar|bar.?size/i,
      o => o.values.every(v => /[234]/.test(v) && /inch|"|″/.test(v)));
    const fitIndex = optionIndex(product, settings.fit_option, /fit|for use|tube|carrier|version/i);
    const fitValue = settings[state.capacity === 'Double' ? 'fit_double' : state.build === 'Original' ? 'fit_original' : 'fit_deluxe'];
    return {valid: widthIndex !== -2 && fitIndex !== -2 && !(fitIndex >= 0 && fitIndex === widthIndex), widthIndex, fitIndex, fitValue};
  }
  function kitVariants(product, settings, state, selections = {}) {
    const d = kitDimensions(product, settings, state);
    if (!d.valid) return [];
    return product.variants.filter(v => v.options.every((value, i) => {
      if (i === d.fitIndex) return normalize(value) === normalize(d.fitValue);
      return selections[i] == null || normalize(value) === normalize(selections[i]);
    }));
  }
  function validQuantity(variant, quantity) {
    const r = variant?.quantityRule || {min: 1, max: null, increment: 1};
    return Number.isInteger(quantity) && quantity >= r.min && (!r.max || quantity <= r.max) && (quantity - r.min) % r.increment === 0;
  }
  function cartItems(carrier, kit, quantity, note, noteLabel) {
    if (!carrier?.available || !validQuantity(carrier, quantity) || (kit && (!kit.available || !validQuantity(kit, quantity)))) throw new Error('Invalid selection');
    const main = {id: carrier.id, quantity};
    if (String(note || '').trim()) main.properties = {[noteLabel || 'Custom note']: String(note).trim()};
    return kit ? [main, {id: kit.id, quantity}] : [main];
  }
  const engine = {normalize, buildMatches, optionIndex, findMap, carrierDimensions, carrierVariants, kitDimensions, kitVariants, validQuantity, cartItems};
  if (typeof module !== 'undefined' && module.exports) module.exports = engine;
  if (typeof window === 'undefined') return;
  window.MPRodCarrier = engine;

  const el = (tag, cls, content) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (content != null) node.textContent = content;
    return node;
  };
  class RodBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const data = this.querySelector('[data-mp-data]');
      if (!data) return;
      this.initialized = true;
      this.config = JSON.parse(data.textContent);
      this.settings = this.config.settings;
      this.state = {capacity: this.settings.default_capacity, security: this.settings.default_security, build: this.settings.default_build};
      this.kitSelected = Boolean(this.settings.kit_default && this.config.kit);
      this.hasCapacitySelection = false;
      this.selections = {};
      this.kitSelections = {};
      this.abort = new AbortController();
      this.busy = false;
      this.form = this.querySelector('form');
      this.track = this.querySelector('[data-mp-gallery-track]');
      const signal = this.abort.signal;
      this.addEventListener('change', e => this.onChange(e), {signal});
      this.addEventListener('click', e => this.onClick(e), {signal});
      this.addEventListener('input', e => {
        if (e.target.matches('[data-mp-order-note]')) this.syncNote(e.target.value);
        if (e.target.matches('[data-mp-quantity]')) this.updatePrice();
      }, {signal});
      this.form.addEventListener('submit', e => {e.preventDefault(); this.addToCart(false);}, {signal});
      this.track.addEventListener('scroll', () => this.galleryPosition(), {signal, passive: true});
      this.initForPageProduct();
      this.initFromURL();
      this.refresh(true);
      const sticky = this.querySelector('[data-mp-sticky]');
      if (sticky && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top >= 0;
        });
        this.observer.observe(this.querySelector('[data-mp-purchase]'));
      }
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.initialized = false;
      clearTimeout(this.successTimer);
    }
    initForPageProduct() {
      const maps = this.config.maps.filter(m => m.product?.handle === this.config.pageProductHandle);
      const map = maps.find(m => m.build === this.state.build) || maps.find(m => m.build === 'Any') || maps[0];
      if (!map) return;
      this.state.capacity = map.capacity;
      this.state.security = map.security;
      if (map.build !== 'Any') this.state.build = map.build;
    }
    initFromURL() {
      const variantId = new URL(location.href).searchParams.get('variant');
      if (!variantId) return;
      for (const m of this.config.maps) {
        const v = m.product?.variants.find(v => String(v.id) === variantId);
        if (!v) continue;
        this.state.capacity = m.capacity;
        this.state.security = m.security;
        if (m.build !== 'Any') this.state.build = m.build;
        else if (m.capacity === 'Single') {
          const i = optionIndex(m.product, m.buildOption, /build|version|tube|style/i);
          if (i >= 0) this.state.build = buildMatches(v.options[i], m.originalValue) ? 'Original' : 'Deluxe';
        }
        const dimensions = carrierDimensions(m, this.state);
        v.options.forEach((value, i) => {if (i !== dimensions.buildIndex) this.selections[i] = value;});
        this.currentMapId = m.id;
        this.hasCapacitySelection = true;
        return;
      }
    }
    choiceLabel(group, value) {
      const input = [...this.querySelectorAll(`[data-mp-choice="${group}"]`)].find(n => n.value === value);
      return input?.closest('label').querySelector('strong')?.textContent || value;
    }
    colorLabel(value) {
      return this.config.swatches.find(s => normalize(s.name) === normalize(value))?.name || value || '';
    }
    onChange(event) {
      if (this.busy) return;
      const target = event.target;
      if (target.matches('[data-mp-choice]')) {
        if (target.dataset.mpChoice === 'capacity') this.hasCapacitySelection = true;
        this.state[target.dataset.mpChoice] = target.value;
        this.refresh(true);
      } else if (target.matches('[data-mp-option-index]')) {
        this.selections[target.dataset.mpOptionIndex] = target.value;
        this.refresh(false);
      } else if (target.matches('[data-mp-mount]')) {
        this.kitSelected = target.value === 'kit'; this.refresh(false);
      } else if (target.matches('[data-mp-kit-index]')) {
        this.kitSelections[target.dataset.mpKitIndex] = target.value; this.refresh(false);
      } else if (target.matches('[data-mp-quantity]')) this.updatePrice();
    }
    onClick(event) {
      const option = event.target.closest('label')?.querySelector('input[data-mp-choice="capacity"]') || event.target.closest('input[data-mp-choice="capacity"]');
      if (!this.busy && !this.hasCapacitySelection && option && !option.disabled && this.state.capacity === option.value) {
        this.hasCapacitySelection = true;
        this.refresh(false);
      }
      const measure = event.target.closest('[data-mp-measure-toggle]');
      if (measure) {
        const guide = this.querySelector('[data-mp-measure-guide]');
        guide.hidden = !guide.hidden;
        measure.setAttribute('aria-expanded', String(!guide.hidden));
        measure.querySelector('[data-mp-measure-sign]').textContent = guide.hidden ? '+' : '−';
        return;
      }
      const button = event.target.closest('[data-mp-add], [data-mp-checkout], [data-mp-thumb]');
      if (!button) return;
      if (button.matches('[data-mp-thumb]')) {
        this.goToSlide(Number(button.dataset.mpThumb)); return;
      }
      if (button.type !== 'submit') {
        event.preventDefault(); this.addToCart(button.hasAttribute('data-mp-checkout'));
      }
    }
    refresh(reset) {
      this.map = findMap(this.config.maps, this.state);
      const dimensions = carrierDimensions(this.map, this.state);
      const previousColor = this.lastColor;
      if (reset && this.currentMapId !== this.map?.id) this.selections = {};
      this.dimensions = dimensions;
      if (this.map?.product && dimensions.valid) {
        const variants = carrierVariants(this.map, this.state);
        const seed = variants.find(v => v.available && (previousColor == null || normalize(v.options[dimensions.colorIndex]) === normalize(previousColor)))
          || variants.find(v => v.available) || variants[0];
        if (seed) seed.options.forEach((value, i) => {
          const exists = this.map.product.options[i].values.includes(this.selections[i]);
          if (i !== dimensions.buildIndex && !exists) this.selections[i] = value;
        });
        // A build change must keep the variant's build dimension authoritative.
        this.variant = carrierVariants(this.map, this.state, this.selections)[0] || null;
      } else this.variant = null;
      this.lastColor = this.selections[dimensions.colorIndex];
      this.renderChoices();
      this.renderCarrierOptions();
      this.renderKit();
      const galleryKey = this.hasCapacitySelection ? this.state.capacity : 'intro';
      if (this.galleryKey !== galleryKey || !this.galleryInitialized) {
        this.renderGallery();
        if (this.hasCapacitySelection) this.selectGalleryImage();
        this.galleryKey = galleryKey;
      }
      this.currentMapId = this.map?.id;
      const desc = this.querySelector('[data-mp-description]');
      if (desc) desc.innerHTML = this.map?.descriptionOverride || this.map?.product?.description || '';
      const isDeluxe = this.state.capacity === 'Single' && this.state.build === 'Deluxe';
      const includes = this.querySelector('[data-mp-included]');
      if (includes) includes.innerHTML = (isDeluxe ? this.map?.includedDeluxe : this.map?.includedOriginal) || this.map?.includedOriginal || '';
      const tube = this.querySelector('[data-mp-tube-note]');
      if (tube) tube.textContent = (isDeluxe ? this.map?.tubeDeluxe : this.map?.tubeOriginal) || '';
      this.querySelector('[data-mp-variant-id]').value = this.variant?.id || '';
      const q = this.querySelector('[data-mp-quantity]');
      if (reset && this.variant && !validQuantity(this.variant, Number(q.value))) q.value = this.variant.quantityRule?.min || 1;
      q.min = this.variant?.quantityRule?.min || 1;
      q.step = this.variant?.quantityRule?.increment || 1;
      if (this.variant?.quantityRule?.max) q.max = this.variant.quantityRule.max; else q.removeAttribute('max');
      this.updatePrice();
      const warning = this.querySelector('[data-mp-config-warning]');
      if (warning) {
        warning.hidden = this.config.maps.every(m => m.product) && dimensions.valid;
        if (!warning.hidden && this.map?.product && !dimensions.valid) warning.textContent = 'This product needs its exact backend option names/values configured. When Original and Deluxe are separate products, use separate Product mapping blocks with the specific build selected.';
      }
      this.dispatchEvent(new CustomEvent('mp:variant-change', {bubbles: true, detail: {product: this.map?.product, variant: this.variant, kitVariant: this.kitVariant}}));
    }
    renderChoices() {
      for (const input of this.querySelectorAll('[data-mp-choice]')) {
        const group = input.dataset.mpChoice;
        input.checked = this.state[group] === input.value;
        const candidates = this.config.maps.filter(m => m.product && m.capacity === (group === 'capacity' ? input.value : this.state.capacity)
          && m.security === (group === 'security' ? input.value : this.state.security)
          && (group !== 'build' || m.build === 'Any' || m.build === input.value));
        input.disabled = !candidates.some(m => {
          const state = {...this.state, [group]: input.value};
          if (group !== 'build' && m.capacity === 'Single') return ['Original', 'Deluxe'].some(build => (m.build === 'Any' || m.build === build) && carrierVariants(m, {...state, capacity: m.capacity, security: m.security, build}).some(v => v.available));
          return carrierVariants(m, {...state, capacity: m.capacity, security: m.security}).some(v => v.available);
        });
        input.closest('label').classList.toggle('is-selected', input.checked);
        input.closest('label').classList.toggle('is-disabled', input.disabled);
      }
      this.querySelector('[data-mp-group="build"]').hidden = this.state.capacity === 'Double';
      this.querySelector('[data-mp-group="color"]').hidden = this.dimensions.colorIndex < 0;
      let number = 1;
      for (const group of this.querySelectorAll('[data-mp-group]')) {
        if (!group.hidden) group.querySelector('[data-mp-step-number]').textContent = number++;
      }
      for (const group of ['capacity', 'security', 'build']) this.querySelector(`[data-mp-group-value="${group}"]`).textContent = this.choiceLabel(group, this.state[group]);
      this.querySelector('[data-mp-group-value="color"]').textContent = this.colorLabel(this.lastColor);
      this.querySelector('[data-mp-build-note]').textContent = this.settings[this.state.build === 'Original' ? 'original_note' : 'deluxe_note'];
      this.querySelector('[data-mp-group-value="mount"]').textContent = this.kitSelected ? this.settings.kit_label : this.settings.self_mount_label;
    }
    selectField(option, index, value, dataset, options, label) {
      const wrapper = el('div', 'mp-live-option');
      const id = `mp-${this.config.sectionId}-${dataset}-${index}`;
      const caption = el('label', 'mp-small', label || option.name); caption.htmlFor = id;
      const select = el('select', 'mp-select'); select.id = id;
      select.dataset[dataset] = index;
      for (const v of option.values) {
        const opt = el('option', '', v); opt.value = v; opt.selected = value === v;
        opt.disabled = !options.some(variant => normalize(variant.options[index]) === normalize(v) && variant.available);
        select.append(opt);
      }
      wrapper.append(caption, select);
      return wrapper;
    }
    renderCarrierOptions() {
      const colors = this.querySelector('[data-mp-colors]');
      const others = this.querySelector('[data-mp-other-options]');
      const focusedIndex = document.activeElement?.dataset.mpOptionIndex;
      const focusedValue = document.activeElement?.value;
      colors.replaceChildren(); others.replaceChildren();
      if (!this.map?.product || !this.dimensions.valid) return;
      const product = this.map.product;
      product.options.forEach((option, index) => {
        if (index === this.dimensions.buildIndex || (option.name === 'Title' && option.values.length === 1 && option.values[0] === 'Default Title')) return;
        const checkSelections = {...this.selections}; delete checkSelections[index];
        const candidates = carrierVariants(this.map, this.state, checkSelections);
        if (index === this.dimensions.colorIndex) {
          for (const color of option.values) {
            const label = el('label', 'mp-swatch');
            const input = el('input', 'mp-swatch-input'); input.type = 'radio'; input.name = `mp-color-${this.config.sectionId}`;
            input.value = color; input.dataset.mpOptionIndex = index; input.checked = this.selections[index] === color;
            input.disabled = !candidates.some(v => normalize(v.options[index]) === normalize(color) && v.available);
            const swatch = el('span', 'mp-swatch-dot');
            const customColor = this.config.swatches.find(s => normalize(s.name) === normalize(color))?.color;
            swatch.style.backgroundColor = customColor || (CSS.supports('color', color) ? color : '#DED6CB');
            const caption = el('span', 'mp-swatch-name', this.colorLabel(color));
            label.title = this.colorLabel(color) + (input.disabled ? ` — ${this.settings.sold_out_label}` : '');
            label.classList.toggle('is-selected', input.checked); label.classList.toggle('is-disabled', input.disabled);
            label.append(input, swatch, caption); colors.append(label);
          }
        } else others.append(this.selectField(option, index, this.selections[index], 'mpOptionIndex', candidates));
      });
      // Preserve keyboard focus when a variant change rebuilds the option controls.
      if (focusedIndex != null) [...this.querySelectorAll('[data-mp-option-index]')].find(n => n.dataset.mpOptionIndex === focusedIndex && (n.tagName === 'SELECT' || n.value === focusedValue))?.focus({preventScroll: true});
    }
    renderKit() {
      const p = this.config.kit, d = kitDimensions(p, this.settings, this.state);
      const dimensionsKey = [this.state.capacity, this.state.build, d.fitValue].join('|');
      if (this.kitDimensionsKey !== dimensionsKey) this.kitSelections = {};
      this.kitDimensionsKey = dimensionsKey;
      if (p && d.valid) {
        const variants = kitVariants(p, this.settings, this.state);
        const seed = variants.find(v => v.available) || variants[0];
        if (seed) seed.options.forEach((value, i) => {
          if (i !== d.fitIndex && !p.options[i].values.includes(this.kitSelections[i])) this.kitSelections[i] = value;
        });
        this.kitVariant = kitVariants(p, this.settings, this.state, this.kitSelections)[0] || null;
      } else this.kitVariant = null;
      const width = this.querySelector('[data-mp-width-options]'), others = this.querySelector('[data-mp-kit-other-options]');
      const focusedIndex = document.activeElement?.dataset.mpKitIndex;
      const focusedValue = document.activeElement?.value;
      width.replaceChildren(); others.replaceChildren();
      if (p && d.valid) p.options.forEach((option, index) => {
        if (index === d.fitIndex || (option.name === 'Title' && option.values[0] === 'Default Title')) return;
        const selections = {...this.kitSelections}; delete selections[index];
        const candidates = kitVariants(p, this.settings, this.state, selections);
        if (index === d.widthIndex) {
          const field = el('fieldset', 'mp-live-option mp-live-option--width');
          field.setAttribute('aria-labelledby', `mp-width-label-${this.config.sectionId}`);
          const grid = el('div', 'mp-width-grid');
          for (const value of option.values) {
            const label = el('label', 'mp-width-choice');
            const input = el('input', 'mp-option-input'); input.type = 'radio'; input.name = `mp-width-${this.config.sectionId}`;
            input.value = value; input.dataset.mpKitIndex = index; input.checked = this.kitSelections[index] === value;
            input.disabled = !candidates.some(v => normalize(v.options[index]) === normalize(value) && v.available);
            label.classList.toggle('is-selected', input.checked); label.classList.toggle('is-disabled', input.disabled);
            label.append(input, el('span', '', value)); grid.append(label);
          }
          field.append(grid); width.append(field);
        } else others.append(this.selectField(option, index, this.kitSelections[index], 'mpKitIndex', candidates));
      });
      const widthHeading = this.querySelector('[data-mp-width-heading]');
      if (widthHeading) {
        widthHeading.hidden = !p || !d.valid || d.widthIndex < 0;
        widthHeading.querySelector('[data-mp-width-value]').textContent = this.kitSelections[d.widthIndex] || '';
      }
      if (focusedIndex != null) [...this.querySelectorAll('[data-mp-kit-index]')].find(n => n.dataset.mpKitIndex === focusedIndex && (n.tagName === 'SELECT' || n.value === focusedValue))?.focus({preventScroll:true});
      this.querySelector('[data-mp-kit-options]').hidden = !this.kitSelected;
      this.querySelector('[data-mp-kit-card]').classList.toggle('is-selected', this.kitSelected);
      this.querySelector('[data-mp-kit-fit]').textContent = `${this.settings.fit_label}: ${d.fitValue || ''}`;
      this.querySelector('[data-mp-kit-price]').textContent = this.kitVariant ? '+ ' + this.money(this.kitVariant.price) : this.settings.unavailable_label;
      for (const input of this.querySelectorAll('[data-mp-mount]')) {
        input.checked = (input.value === 'kit') === this.kitSelected;
        if (input.value === 'kit') input.disabled = !p;
        input.closest('label').classList.toggle('is-selected', input.checked);
      }
    }
    money(cents) {
      return new Intl.NumberFormat(this.config.locale || 'en', {style: 'currency', currency: this.config.currency || 'USD'}).format(cents / 100);
    }
    updatePrice() {
      const quantity = Number(this.querySelector('[data-mp-quantity]').value);
      const total = this.variant ? (this.variant.price + (this.kitSelected && this.kitVariant ? this.kitVariant.price : 0)) * (Number.isFinite(quantity) ? quantity : 1) : null;
      for (const output of this.querySelectorAll('[data-mp-total]')) output.textContent = total == null ? '—' : this.money(total);
      const pieces = [this.choiceLabel('capacity', this.state.capacity), this.choiceLabel('security', this.state.security)];
      if (this.state.capacity === 'Single') pieces.push(this.choiceLabel('build', this.state.build));
      if (this.lastColor) pieces.push(this.colorLabel(this.lastColor));
      if (this.kitSelected) pieces.push('+ ' + (this.config.kit?.title || this.settings.kit_label));
      const summary = pieces.join(' · ');
      for (const output of this.querySelectorAll('[data-mp-summary]')) output.textContent = summary;
      let message = '';
      if (!this.variant || this.map?.product?.requiresSellingPlan) message = this.settings.unavailable_message;
      else if (!this.variant.available) message = this.settings.sold_out_label;
      else if (this.kitSelected && (!this.kitVariant?.available || this.config.kit?.requiresSellingPlan)) message = this.settings.kit_unavailable_message;
      else if (!validQuantity(this.variant, quantity) || (this.kitSelected && !validQuantity(this.kitVariant, quantity))) message = this.settings.quantity_error;
      this.canAdd = !message;
      this.setError(message);
      for (const button of this.querySelectorAll('[data-mp-add], [data-mp-checkout]')) {
        button.disabled = !this.canAdd || this.busy;
        button.setAttribute('aria-busy', String(this.busy));
      }
      for (const label of this.querySelectorAll('[data-mp-add-label]')) label.textContent = this.busy ? this.settings.loading_label : !this.variant ? this.settings.unavailable_label : !this.variant.available ? this.settings.sold_out_label : this.settings.add_label;
    }
    setError(message) {
      const output = this.querySelector('[data-mp-error]'); output.textContent = message || ''; output.hidden = !message;
    }
    renderGallery() {
      const editorial = this.querySelector('[data-mp-editorial-gallery]').content.cloneNode(true);
      const introImages = [...editorial.children].filter(slide => slide.querySelector('img.mp-image')).slice(0, 3);
      const introSlides = [...editorial.children].filter(slide => introImages.includes(slide) || slide.querySelector('video'));
      const imageMap = this.hasCapacitySelection ? findMap(this.config.maps, {
        capacity: this.state.capacity, security: this.state.capacity === 'Single' ? 'Basic' : 'Locking', build: 'Original'
      }) : null;
      const product = imageMap && [...this.querySelectorAll('[data-mp-product-gallery]')].find(t => t.dataset.mpProductGallery === imageMap.id);
      const configured = imageMap && [...this.querySelectorAll('[data-mp-config-gallery]')].find(t => t.dataset.mpConfigGallery === imageMap.id);
      const productPhoto = this.settings.show_product_media && product?.content.querySelector('.mp-slide:has(img.mp-image)');
      const fallbackPhoto = configured?.content.querySelector('[data-mp-build-image="Original"]');
      const representative = (productPhoto || fallbackPhoto)?.cloneNode(true);
      if (representative) representative.dataset.mpCapacityImage = this.state.capacity;
      this.track.querySelectorAll('video').forEach(v => v.pause());
      this.track.replaceChildren(...(!this.settings.editorial_first && representative ? [representative] : []), ...introSlides, ...(this.settings.editorial_first && representative ? [representative] : []));
      const image = this.track.querySelector('img.mp-image');
      if (image) {image.loading = 'eager'; image.setAttribute('fetchpriority', 'high');}
      const thumbs = this.querySelector('[data-mp-gallery-thumbs]'); thumbs.replaceChildren();
      for (const [index, slide] of [...this.track.children].entries()) {
        const thumb = el('button', 'mp-gallery-thumb'); thumb.type = 'button'; thumb.dataset.mpThumb = index;
        thumb.setAttribute('aria-label', `${index + 1}. ${slide.dataset.alt || this.settings.gallery_label || ''}`);
        if (slide.dataset.thumb) {const img = el('img'); img.src = slide.dataset.thumb; img.alt = ''; img.loading = 'lazy'; thumb.append(img);}
        else thumb.append(el('span', '', slide.querySelector('video') ? '▶' : String(index + 1)));
        thumbs.append(thumb);
      }
      this.galleryInitialized = true;
      this.querySelector('.mp-product-media').classList.toggle('mp-product-media--sticky', this.settings.sticky_gallery);
      this.track.scrollLeft = 0; this.galleryPosition();
    }
    selectGalleryImage() {
      const slides = [...this.track.children];
      let index = slides.findIndex(s => s.dataset.mpCapacityImage === this.state.capacity);
      if (index < 0) index = 0;
      const image = slides[index]?.querySelector('img.mp-image');
      if (image) {image.loading = 'eager'; image.setAttribute('fetchpriority', 'high');}
      this.goToSlide(index, true);
    }
    goToSlide(index, immediate = false) {
      this.track.scrollTo({left: index * this.track.clientWidth, behavior: immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      if (immediate) this.galleryPosition();
    }
    galleryPosition() {
      const current = Math.round(this.track.scrollLeft / (this.track.clientWidth || 1));
      const total = this.track.children.length;
      this.querySelector('[data-mp-gallery-count]').textContent = total ? `${current + 1} / ${total}` : '';
      for (const [index, thumb] of [...this.querySelectorAll('[data-mp-thumb]')].entries()) {
        thumb.classList.toggle('is-selected', index === current); thumb.setAttribute('aria-current', index === current ? 'true' : 'false');
      }
      for (const [index, slide] of [...this.track.children].entries()) if (index !== current) slide.querySelectorAll('video').forEach(video => video.pause());
    }
    syncNote(value) {
      for (const input of document.querySelectorAll('[data-mp-custom-note], [data-mp-order-note]')) if (input.value !== value) input.value = value;
    }
    async addToCart(checkout) {
      if (this.busy || !this.canAdd) return;
      const variant = this.variant;
      const items = cartItems(variant, this.kitSelected ? this.kitVariant : null, Number(this.querySelector('[data-mp-quantity]').value), this.querySelector('[data-mp-order-note]').value, this.settings.note_property);
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const payload = {items};
      if (cart && !checkout) {
        payload.sections = cart.getSectionsToRender().map(s => s.id);
        payload.sections_url = location.pathname;
        cart.setActiveElement?.(document.activeElement);
      }
      this.busy = true; this.updatePrice(); this.setError('');
      let cartError = '';
      // Prevent option changes while the cart request is pending.
      const controls = [...this.querySelectorAll('[data-mp-choice], [data-mp-option-index], [data-mp-kit-index], [data-mp-mount], [data-mp-quantity]')];
      const enabledControls = controls.filter(c => !c.disabled); enabledControls.forEach(c => c.disabled = true);
      const CartLinesUpdateEvent = window.StandardEvents?.CartLinesUpdateEvent;
      const deferred = CartLinesUpdateEvent?.createPromise();
      if (deferred) {
        deferred.promise.catch(() => {});
        this.dispatchEvent(new CartLinesUpdateEvent({action:'add',context:'product',lines:items.map(i=>({merchandiseId:String(i.id),quantity:i.quantity})),promise:deferred.promise}));
      }
      let added = false;
      try {
        const response = await fetch(this.config.cartAddUrl, {method: 'POST', headers: {'Content-Type':'application/json','Accept':'application/json','X-Requested-With':'XMLHttpRequest'}, body: JSON.stringify(payload)});
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || this.settings.error_label);
        added = true;
        if (deferred) fetch(this.config.cartUrl + '.js', {headers:{Accept:'application/json'}}).then(r=>r.json()).then(cartData=>{
          if (!cartData?.currency) throw new Error('Missing currency in cart response');
          deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cartData)});
        }).catch(error=>deferred.reject(error));
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, {source: 'mp-rod-carrier', productVariantId: variant.id, cartData: result});
        document.dispatchEvent(new CustomEvent('mp:cart-added', {detail: {items, result}}));
        if (checkout) {location.assign((window.Shopify?.routes?.root || '/') + 'checkout'); return;}
        const first = result.items?.[0] || result;
        const sectionsValid = cart && payload.sections.every(id => typeof result.sections?.[id] === 'string');
        if (sectionsValid) {
          try {cart.classList.remove('is-empty'); cart.renderContents({...result, ...first, sections:result.sections});}
          catch (_) {location.assign(this.config.cartUrl); return;}
        } else {location.assign(this.config.cartUrl); return;}
        this.querySelector('[data-mp-status]').textContent = this.settings.added_label;
        clearTimeout(this.successTimer);
        this.successTimer = setTimeout(() => {if (this.isConnected) this.querySelector('[data-mp-status]').textContent = '';}, 4000);
      } catch (error) {
        cartError = error.message || this.settings.error_label;
        deferred?.reject(error);
        const CartErrorEvent = window.StandardEvents?.CartErrorEvent;
        if (CartErrorEvent) this.dispatchEvent(new CartErrorEvent({error:cartError,code:'CART_ERROR'}));
        this.setError(cartError);
      } finally {
        this.busy = false;
        enabledControls.forEach(c => c.disabled = false);
        this.updatePrice();
        // Cart failures remain visible, including failures after inventory changed on Shopify.
        if (!added && this.canAdd) this.setError(cartError);
      }
    }
  }
  if (!customElements.get('mp-rod-builder')) customElements.define('mp-rod-builder', RodBuilder);

  class CartDesign extends HTMLElement {
    connectedCallback() {
      this.cart = document.querySelector('cart-drawer');
      if (!this.cart || this.observer) return;
      this.labels = JSON.parse(this.querySelector('[data-mp-cart-labels]').textContent);
      this.events = new AbortController();
      this.cart.classList.add('mp-designed-cart');
      this.observer = new MutationObserver(() => this.enhance());
      this.observer.observe(this.cart, {childList:true, subtree:true});
      this.cart.addEventListener('click', event => {
        if (!event.target.closest('[data-mp-cart-continue]')) return;
        event.preventDefault(); this.cart.close?.();
      }, {signal:this.events.signal});
      const preview = event => {
        const section = this.closest('[id^="shopify-section-"]');
        if (section?.contains(event.target) || event.detail?.sectionId === section?.id.replace('shopify-section-', '')) this.cart.open?.();
      };
      document.addEventListener('shopify:section:select', preview, {signal:this.events.signal});
      document.addEventListener('shopify:block:select', preview, {signal:this.events.signal});
      this.enhance();
    }
    disconnectedCallback() {
      this.observer?.disconnect(); this.observer = null;
      this.events?.abort();
      this.cart?.classList.remove('mp-designed-cart');
      this.cart?.querySelectorAll('[data-mp-cart-extras], [data-mp-cart-security], [data-mp-cart-payments], .mp-cart-count, .mp-cart-remove-label').forEach(node => node.remove());
    }
    setText(node, value) {const text = String(value ?? ''); if (node && node.textContent !== text) node.textContent = text;}
    cloneTemplate(selector, target, marker) {
      const template = this.querySelector(selector);
      if (target && template?.content.childElementCount && !target.querySelector(marker)) target.append(template.content.cloneNode(true));
    }
    enhance() {
      const cart = this.cart;
      const empty = cart.classList.contains('is-empty');
      const heading = cart.querySelector('.drawer__heading');
      this.setText(heading, this.labels.heading);
      const dialog = cart.querySelector('.drawer__inner');
      if (dialog && dialog.getAttribute('aria-label') !== (this.labels.heading || 'Cart')) dialog.setAttribute('aria-label', this.labels.heading || 'Cart');
      if (heading) {
        let count = heading.parentElement.querySelector('.mp-cart-count');
        if (!count) {count = el('span', 'mp-cart-count'); heading.after(count);}
        const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
        const countText = bubble?.querySelector('[aria-hidden="true"]')?.textContent || bubble?.querySelector('.visually-hidden')?.textContent || bubble?.textContent;
        const quantity = empty ? 0 : Number(countText?.match(/\d+/)?.[0] || [...cart.querySelectorAll('.quantity__input')].reduce((n, input) => n + Number(input.value || 0), 0));
        this.setText(count, `${quantity} ${(quantity === 1 ? this.labels.item : this.labels.items) || ''}`);
      }
      this.setText(cart.querySelector('.cart__empty-text'), this.labels.empty);
      const continueButton = cart.querySelector('.cart-drawer__empty-content a.button');
      if (continueButton) {continueButton.dataset.mpCartContinue = ''; this.setText(continueButton, this.labels.continue);}
      for (const button of cart.querySelectorAll('cart-remove-button button')) {
        let label = button.querySelector('.mp-cart-remove-label');
        if (!label) {label = el('span', 'mp-cart-remove-label'); button.append(label);}
        this.setText(label, this.labels.remove);
      }
      for (const input of cart.querySelectorAll('.quantity__input')) {
        const minimum = input.dataset.min || '1';
        if (input.min !== minimum) input.min = minimum;
        input.closest('quantity-input')?.validateQtyRules?.();
      }
      this.setText(cart.querySelector('.totals__total'), this.labels.subtotal);
      this.setText(cart.querySelector('.tax-note'), this.labels.shipping);
      const checkout = cart.querySelector('#CartDrawer-Checkout');
      if (checkout) {
        let label = checkout.querySelector('[data-mp-cart-checkout-label]');
        if (!label) {
          label = el('span'); label.dataset.mpCartCheckoutLabel = '';
          checkout.replaceChildren(this.querySelector('[data-mp-cart-lock]').content.cloneNode(true), label);
        }
        this.setText(label, this.labels.checkout);
      }
      if (empty) {
        cart.querySelectorAll('[data-mp-cart-extras], [data-mp-cart-security], [data-mp-cart-payments]').forEach(node => node.remove());
      } else {
        this.cloneTemplate('[data-mp-cart-extras-template]', cart.querySelector('cart-drawer-items'), '[data-mp-cart-extras]');
        const footer = cart.querySelector('.drawer__footer');
        this.cloneTemplate('[data-mp-cart-security-template]', footer, '[data-mp-cart-security]');
        this.cloneTemplate('[data-mp-cart-payments-template]', footer, '[data-mp-cart-payments]');
      }
    }
  }
  if (!customElements.get('mp-cart-design')) customElements.define('mp-cart-design', CartDesign);

  class CustomerStories extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.mp-quote-track');
      this.toggle = this.querySelector('[data-mp-stories-toggle]');
      if (!this.track || !this.toggle || this.events) return;
      this.events = new AbortController();
      const listen = (target, event, handler, options = {}) => target.addEventListener(event, handler, {...options, signal:this.events.signal});
      this.cards = [...this.track.children].filter(card => !card.hasAttribute('data-mp-story-clone'));
      this.speed = Math.min(32, Math.max(4, Number(this.dataset.scrollSpeed) || 12));
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.inView = !('IntersectionObserver' in window);
      this.userPaused = false;
      this.hovered = false;
      this.focused = false;
      this.interacting = false;
      listen(this, 'pointerenter', event => {if (event.pointerType !== 'touch') {this.hovered = true; this.update();}});
      listen(this, 'pointerleave', () => {this.hovered = false; this.update();});
      listen(this.track, 'focusin', () => {this.focused = true; this.update();});
      listen(this.track, 'focusout', event => {this.focused = this.track.contains(event.relatedTarget); this.update();});
      listen(this.track, 'pointerdown', () => {clearTimeout(this.interactionTimer); this.interacting = true; this.update();});
      const release = () => {if (this.interacting) this.pauseInteraction();};
      listen(window, 'pointerup', release);
      listen(window, 'pointercancel', release);
      listen(this.track, 'wheel', () => this.pauseInteraction(), {passive:true});
      listen(this.toggle, 'click', () => {this.userPaused = !this.userPaused; this.update();});
      listen(document, 'visibilitychange', () => this.update());
      listen(this.motion, 'change', () => this.measure());
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this.track);
      } else listen(window, 'resize', () => this.measure());
      if ('IntersectionObserver' in window) {
        this.viewObserver = new IntersectionObserver(entries => {this.inView = entries[0].isIntersecting; this.update();});
        this.viewObserver.observe(this);
      }
      this.measure();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.resizeObserver?.disconnect();
      this.viewObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.interactionTimer);
      this.frame = null;
      this.track?.querySelectorAll('[data-mp-story-clone]').forEach(card => card.remove());
    }
    pauseInteraction() {
      clearTimeout(this.interactionTimer);
      this.interacting = true;
      this.update();
      this.interactionTimer = setTimeout(() => {this.interacting = false; this.update();}, 6000);
    }
    measure() {
      const enabled = this.dataset.autoScroll === 'true' && !this.motion.matches && !window.Shopify?.designMode && this.cards.length > 1;
      const first = this.cards[0], last = this.cards[this.cards.length - 1];
      const overflowing = first && last && last.offsetLeft + last.offsetWidth - first.offsetLeft > this.track.clientWidth + 1;
      this.automatic = Boolean(enabled && overflowing);
      if (this.automatic && !this.track.querySelector('[data-mp-story-clone]')) {
        for (const card of this.cards) {
          const clone = card.cloneNode(true);
          clone.setAttribute('data-mp-story-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.inert = true;
          for (const element of [clone, ...clone.querySelectorAll('*')]) {
            element.removeAttribute('id');
            element.removeAttribute('data-shopify-editor-block');
            element.removeAttribute('data-shopify-editor-block-id');
          }
          this.track.append(clone);
        }
      } else if (!this.automatic) this.track.querySelectorAll('[data-mp-story-clone]').forEach(card => card.remove());
      this.loopWidth = this.track.querySelector('[data-mp-story-clone]')?.offsetLeft - (first?.offsetLeft || 0);
      this.classList.toggle('is-automatic', this.automatic);
      this.toggle.hidden = !this.automatic;
      this.update();
    }
    update() {
      this.toggle.textContent = this.userPaused ? this.toggle.dataset.play : this.toggle.dataset.pause;
      this.toggle.setAttribute('aria-pressed', String(this.userPaused));
      const running = this.automatic && this.loopWidth > 0 && this.inView && !document.hidden && !this.userPaused && !this.hovered && !this.focused && !this.interacting;
      if (!running) {cancelAnimationFrame(this.frame); this.frame = null; return;}
      if (this.frame) return;
      this.position = this.track.scrollLeft;
      this.lastTime = null;
      const step = now => {
        if (this.lastTime !== null) this.position = (this.position + this.speed * Math.min((now - this.lastTime) / 1000, .1)) % this.loopWidth;
        this.lastTime = now;
        this.track.scrollLeft = this.position;
        this.frame = requestAnimationFrame(step);
      };
      this.frame = requestAnimationFrame(step);
    }
  }
  if (!customElements.get('mp-customer-stories')) customElements.define('mp-customer-stories', CustomerStories);

  function activateDeferredVideo(video, play = true) {
    if (!video.dataset.mpVideoLoaded) {
      for (const source of [video, ...video.querySelectorAll('[data-mp-src]')]) {
        if (!source.dataset.mpSrc) continue;
        source.setAttribute('src', source.dataset.mpSrc);
        source.removeAttribute('data-mp-src');
      }
      video.dataset.mpVideoLoaded = 'true';
      video.load();
    }
    if (play && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) video.play().catch(() => {});
  }
  const observedVideos = new WeakSet();
  const videoObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.target.isConnected) {videoObserver.unobserve(entry.target); continue;}
      if (!entry.isIntersecting) continue;
      activateDeferredVideo(entry.target);
      videoObserver.unobserve(entry.target);
    }
  }, {rootMargin: '200px 0px'}) : null;

  function initializeSections(scope = document) {
    const cartLink = scope.querySelector('.mp-header-cart');
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartLink && cartLink.getAttribute('aria-haspopup') !== 'dialog' && cartDrawer?.setHeaderCartIconAccessibility) cartDrawer.setHeaderCartIconAccessibility();
    for (const video of scope.querySelectorAll('[data-mp-lazy-video]')) {
      if (observedVideos.has(video)) continue;
      observedVideos.add(video);
      if (videoObserver) videoObserver.observe(video);
      else activateDeferredVideo(video, false);
      video.addEventListener('pointerdown', () => activateDeferredVideo(video, false), {once: true});
      video.addEventListener('keydown', () => activateDeferredVideo(video, false), {once: true});
    }
    for (const list of scope.querySelectorAll('[data-mp-review-list]')) {
      const toggle = list.querySelector('[data-mp-review-toggle]');
      if (!toggle || toggle.dataset.initialized) continue;
      toggle.dataset.initialized = 'true';
      const reviews = [...list.querySelectorAll('.mp-review')];
      const initial = Number(list.dataset.initial);
      const show = expanded => {
        reviews.forEach((review, i) => review.hidden = !expanded && i >= initial);
        toggle.textContent = expanded ? toggle.dataset.less : toggle.dataset.more;
        toggle.setAttribute('aria-expanded', String(expanded));
      };
      if (!window.Shopify?.designMode) show(false);
      toggle.addEventListener('click', () => show(toggle.getAttribute('aria-expanded') !== 'true'));
    }
    for (const panel of scope.querySelectorAll('[data-mp-video-panel]')) {
      const video = panel.querySelector('video'), button = panel.querySelector('[data-mp-video-toggle]');
      if (!video || !button || button.dataset.initialized) continue;
      button.dataset.initialized = 'true';
      const update = () => {button.textContent = video.paused ? '▶' : '❚❚';button.setAttribute('aria-label',video.paused ? button.dataset.play : button.dataset.pause);};
      button.addEventListener('click', () => {if(video.paused) {activateDeferredVideo(video, false); video.play().catch(()=>{});} else video.pause();});
      video.addEventListener('play',update);video.addEventListener('pause',update);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {video.autoplay = false; video.pause();}
      update();
    }
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-mp-save-note]');
    if (!button) return;
    const panel = button.closest('.mp-custom-panel');
    const note = panel.querySelector('[data-mp-custom-note]').value;
    const builder = document.querySelector('mp-rod-builder');
    builder?.syncNote(note);
    panel.querySelector('[data-mp-note-status]').textContent = button.dataset.saved;
  });
  document.addEventListener('input', event => {
    if (event.target.matches('[data-mp-custom-note]')) {
      document.querySelector('mp-rod-builder')?.syncNote(event.target.value);
      event.target.closest('.mp-custom-panel')?.querySelector('[data-mp-note-status]')?.replaceChildren();
    }
  });
  document.addEventListener('shopify:section:load', event => initializeSections(event.target));
  initializeSections();
})();
